import { useFlags } from 'launchdarkly-react-client-sdk';
import React from 'react';

import HeaderBreadcrumb from 'DesignSystem/features/breadcrumbs/breadcrumb';
import SectionContainer from 'DesignSystem/ui/containers';

import { useEventDetails } from '../event-detail.route';
import { useUnsavedChangesWarning } from '../hooks/use-unsaved-changes-warning';
import EventDetailTabs, { EventDetailTab } from './event-detail-tabs';
import EventDetailsTitle from './event-details-title';
import EventItems from './event-items';
import EventUpdates from './event-updates';
import InternalStrategy from './internal-strategy';
import { UnsavedChangesDialog } from './unsaved-changes-dialog';

export default function EventDetails() {
  const { eventMitigationProject } = useFlags();
  const data = useEventDetails();
  const event = data?.event;
  const [activeTab, setActiveTab] = React.useState<EventDetailTab>('overview');
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const { confirmIfDirty, keepEditing, discard, isPromptOpen } = useUnsavedChangesWarning(hasUnsavedChanges, () =>
    setHasUnsavedChanges(false)
  );

  function handleTabChange(tab: EventDetailTab) {
    if (tab === activeTab) return;
    confirmIfDirty(() => setActiveTab(tab));
  }
  const breadcrumbItems = [
    {
      backToLink: `/disruptions/events`,
      breadcrumbText: 'Events',
    },
    { backToLink: null, breadcrumbText: 'Event Details' },
  ];

  return (
    <div className="h-full bg-background">
      <div className="w-full border-b border-border bg-white">
        <SectionContainer variant="header" className="pt-0">
          <div className="flex flex-row flex-wrap items-center justify-between gap-2 py-4">
            <HeaderBreadcrumb breadcrumbItems={breadcrumbItems} classNames="p-0 border-none" />
          </div>
        </SectionContainer>
      </div>
      {!event ? (
        <SectionContainer>
          <div>Event not found</div>
        </SectionContainer>
      ) : eventMitigationProject ? (
        <>
          <SectionContainer className="pb-0">
            <EventDetailsTitle />
          </SectionContainer>
          <div className="border-b border-border">
            <SectionContainer className="py-0">
              <EventDetailTabs
                activeTab={activeTab}
                onTabChange={handleTabChange}
                itemsCount={event.products_affected}
              />
            </SectionContainer>
          </div>
          <SectionContainer className="flex flex-col gap-4">
            {activeTab === 'overview' ? (
              <>
                <InternalStrategy onUnsavedChangesChange={setHasUnsavedChanges} />
                <EventUpdates />
              </>
            ) : (
              <EventItems />
            )}
          </SectionContainer>
        </>
      ) : (
        <SectionContainer className="flex flex-col gap-4">
          <EventDetailsTitle />
          <EventUpdates />
        </SectionContainer>
      )}
      <UnsavedChangesDialog open={isPromptOpen} onKeepEditing={keepEditing} onDiscard={discard} />
    </div>
  );
}
